import ChatGroup from '@/chat/models/ChatGroup';
import { parseEntities, parseEntity } from '@/shared/converters/commonConverters';
import { formatMoment, parseMoment } from '@/shared/converters/dateConverters';
import AssignmentData from '@/shared/models/AssignmentData';
import User from '@/shared/models/User';
import { isPresent } from '@/shared/utils/typescript';
import Memo, { MemoData } from '../models/Memo';
import MemoTag from '../models/MemoTag';
import { ABSTRACT_SCHEMA } from './taskConverters';

export const SCHEMA = {
  ...ABSTRACT_SCHEMA,
  id: 'id',
  assignedUserId: 'assignedTo',
  deadline: (data: any) => parseMoment(data.deadline),
  createdBy: (data: any) => User.withIdFullNameAndAvatar(data.createdBy, data.createdByName, data.createdByAvatar),
  createdOn: (data: any) => parseMoment(data.createdOn),
  resolvedBy: (data: any) => User.withIdFullNameAndAvatar(data.resolvedBy, data.resolvedByName, data.resolvedByAvatar),
  resolvedOn: (data: any) => parseMoment(data.resolvedOn),
  tag: (data: any) => parseMemoTag(data.memoTag),
  isInvoiced: 'isInvoiced',
  isResolvable: 'isResolvable',
  resolvableBy: parseMemoResolver,
};

const TAG_SCHEMA = {
  id: 'id',
  code: 'code',
  name: 'tag',
};

export function parseMemos(data: any): Array<Memo> {
  return parseEntities(data, SCHEMA, Memo);
}

export function parseMemo(data: any): Memo | null {
  return parseEntity(data, SCHEMA, Memo);
}

export function parseMemoTag(data: any): MemoTag | null {
  if (!data?.tag || !data?.code) return null;
  return parseEntity(data, TAG_SCHEMA, MemoTag);
}

export function parseMemoTags(data: any): Array<MemoTag> {
  return parseEntities(data, TAG_SCHEMA, MemoTag);
}

export function formatMemoData(data: MemoData): object {
  return {
    message: data.description,
    tagId: data.tag?.id ?? null,
    deadline: formatMoment(data.deadline),
    ...formatAssignmentData(data.resolvableBy),
  };
}

function formatAssignmentData(data: AssignmentData): object {
  if (!data) {
    return {
      resolvableBy: null,
      resolvableByGroup: null,
    };
  }
  if ('userId' in data) return { resolvableBy: data.userId };
  if ('groupId' in data) return { resolvableByGroup: data.groupId };
  return {};
}

function parseMemoResolver(data: any): ChatGroup | User | null {
  const id = data.resolvableById;
  if (!isPresent(id)) return null;
  if (data.isResolvableByGroup) return ChatGroup.withData({ id });
  return User.withIdAndFullName(id, data.resolvableBy);
}
